<template>
  <div class="services-tab">
    <b-container fluid>
      <!-- Section: Services Management -->
      <b-row class="mb-4">
        <b-col cols="12">
          <div class="section-header">
            <div class="header-top">
              <h3 class="section-title">
                <i class="fas fa-network-wired mr-2" />
                {{ $t('admcfg.servicesManagement', 'Services Management') }}
              </h3>
              <div class="header-actions">
                <b-button
                  size="sm"
                  variant="primary"
                  @click="showAddServiceModal = true"
                  class="mr-2"
                >
                  <i class="fas fa-plus mr-1" />
                  {{ $t('general.add', 'Add Service') }}
                </b-button>
                <b-button
                  size="sm"
                  variant="secondary"
                  @click="refreshAll"
                  :disabled="isLoading"
                >
                  <i class="fas fa-sync mr-1" :class="{ 'fa-spin': isLoading }" />
                  {{ $t('general.refresh', 'Refresh') }}
                </b-button>
              </div>
            </div>
            <p class="section-description">
              {{ $t('admcfg.servicesDescription', 'Monitor and manage ship connections to services') }}
            </p>
          </div>
        </b-col>
      </b-row>

      <!-- Loading / Error States -->
      <b-row v-if="isLoading" class="mb-4">
        <b-col cols="12">
          <div class="text-center py-5">
            <b-spinner />
            <p class="mt-3">
              {{ $t('general.loading', 'Loading...') }}
            </p>
          </div>
        </b-col>
      </b-row>

      <b-row v-else-if="errorMessage" class="mb-4">
        <b-col cols="12">
          <b-alert show variant="danger">
            <i class="fas fa-exclamation-circle mr-2" />
            {{ errorMessage }}
            <b-button
              size="sm"
              variant="outline-danger"
              class="float-right"
              @click="errorMessage = ''"
            >
              {{ $t('general.close', 'Close') }}
            </b-button>
          </b-alert>
        </b-col>
      </b-row>

      <!-- Services Table -->
      <b-row v-if="!isLoading && devices.length > 0" class="mb-4">
        <b-col cols="12">
          <div class="services-table-wrapper">
            <table class="services-table">
              <thead>
                <tr>
                  <th class="expand-col"></th>
                  <th class="device-id-col" @click="sortBy('device_id')">
                    {{ $t('admcfg.deviceId', 'Device ID') }}
                    <i class="fas fa-sort ml-1" style="font-size: 0.7rem;"></i>
                  </th>
                  <th class="ship-name-col" @click="sortBy('ship_name')">
                    {{ $t('admcfg.shipName', 'Ship Name') }}
                    <i class="fas fa-sort ml-1" style="font-size: 0.7rem;"></i>
                  </th>
                  <th class="service-col">
                    {{ $t('admcfg.service', 'Service') }}
                  </th>
                  <th class="status-col" @click="sortBy('status')">
                    {{ $t('admcfg.status', 'Status') }}
                    <i class="fas fa-sort ml-1" style="font-size: 0.7rem;"></i>
                  </th>
                  <th class="expires-col" @click="sortBy('end_date')">
                    {{ $t('admcfg.expires', 'Expires') }}
                    <i class="fas fa-sort ml-1" style="font-size: 0.7rem;"></i>
                  </th>
                  <th class="actions-col">
                    {{ $t('admcfg.actions', 'Actions') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in tableItems"
                  :key="item.isHeader ? item.device_id : `${item.device_id}-${index}`"
                  :class="getRowClass(item)"
                >
                  <!-- Header Row for Device -->
                  <template v-if="item.isHeader">
                    <td class="expand-cell">
                      <button
                        v-if="item.services.length > 0"
                        class="expand-btn"
                        @click="toggleDeviceExpand(item.device_id)"
                        :aria-label="`Toggle ${item.device_id}`"
                      >
                        <i
                          class="fas"
                          :class="item.isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"
                        />
                      </button>
                    </td>
                    <td class="device-id-cell">
                      <div class="device-id">
                        <i class="fas fa-ship mr-2" />
                        <code>{{ item.device_id }}</code>
                      </div>
                    </td>
                    <td class="ship-name-cell">
                      <div class="ship-name">
                        <span>{{ item.ship_name }}</span>
                      </div>
                    </td>
                    <td class="service-cell">
                      <b-badge variant="info" class="services-count-badge">
                        {{ item.services.length }}
                        {{ item.services.length === 1 ? 'service' : 'services' }}
                      </b-badge>
                    </td>
                    <td class="status-cell">
                      <b-badge :variant="getServicesSummaryVariant(item)" class="status-summary-badge">
                        {{ getServicesSummary(item) }}
                      </b-badge>
                    </td>
                    <td class="expires-cell">
                      <span class="text-muted">—</span>
                    </td>
                    <td class="actions-cell">
                      <div class="action-buttons">
                        <b-button
                          size="sm"
                          variant="primary"
                          title="Add Service"
                          @click="addServiceForDevice(item.device_id)"
                          class="mr-1"
                        >
                          <i class="fas fa-plus" />
                        </b-button>
                      </div>
                    </td>
                  </template>

                  <!-- Service Row (nested under device) -->
                  <template v-else>
                    <td class="expand-cell"></td>
                    <td class="device-id-cell indent-cell">
                      <div class="device-id indent-text">
                        <code>{{ item.device_id }}</code>
                      </div>
                    </td>
                    <td class="ship-name-cell"></td>
                    <td class="service-cell">
                      <div class="service-type-badge">
                        <b-badge variant="primary">
                          {{ item.service_type }}
                        </b-badge>
                      </div>
                    </td>
                    <td class="status-cell">
                      <div class="status-badge">
                        <b-badge :variant="getStatusBadge(item.status)">
                          {{ getStatusInfo(item.status).icon }}
                          {{ item.status }}
                        </b-badge>
                      </div>
                    </td>
                    <td class="expires-cell">
                      <span v-if="item.end_date === 'Unlimited'" class="text-muted">
                        {{ item.end_date }}
                      </span>
                      <span v-else :class="{ 'text-danger': isDateExpired(item.end_date_raw) }">
                        {{ item.end_date }}
                      </span>
                    </td>
                    <td class="actions-cell">
                      <div class="action-buttons">
                        <b-button
                          size="sm"
                          variant="outline-primary"
                          title="Edit"
                          @click="editService(item)"
                          class="mr-1"
                        >
                          <i class="fas fa-edit" />
                        </b-button>

                        <b-button
                          v-if="item.status === 'Active'"
                          size="sm"
                          variant="outline-warning"
                          title="Pause"
                          @click="pauseServiceAction(item)"
                          class="mr-1"
                        >
                          <i class="fas fa-pause" />
                        </b-button>

                        <b-button
                          v-if="item.status === 'Paused'"
                          size="sm"
                          variant="outline-success"
                          title="Resume"
                          @click="resumeServiceAction(item)"
                          class="mr-1"
                        >
                          <i class="fas fa-play" />
                        </b-button>

                        <b-button
                          v-if="item.status !== 'Canceled'"
                          size="sm"
                          variant="outline-danger"
                          title="Unsubscribe"
                          @click="unsubscribeAction(item)"
                        >
                          <i class="fas fa-trash" />
                        </b-button>
                      </div>
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
        </b-col>
      </b-row>

      <!-- Empty State -->
      <b-row v-else-if="!isLoading && devices.length === 0" class="mb-4">
        <b-col cols="12">
          <div class="empty-state">
            <div class="empty-icon">
              <i class="fas fa-inbox" />
            </div>
            <h5>{{ $t('admcfg.noDevices', 'No devices found') }}</h5>
            <p class="text-muted">
              {{ $t('admcfg.noDevicesDescription', 'You have no devices yet. Subscribe to services to get started.') }}
            </p>
          </div>
        </b-col>
      </b-row>

      <!-- Section: Service Health -->
      <b-row class="mt-5">
        <b-col cols="12">
          <div class="section-header">
            <h3 class="section-title">
              <i class="fas fa-heartbeat mr-2" />
              {{ $t('admcfg.serviceHealth', 'Service Health') }}
            </h3>
          </div>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="6" lg="3" class="mb-3">
          <health-card
            service-name="ENC Catalog"
            :status="servicesHealth.Catalog || 'error'"
            icon="fas fa-map"
          />
        </b-col>
        <b-col cols="12" md="6" lg="3" class="mb-3">
          <health-card
            service-name="AIS Service"
            :status="servicesHealth.AIS || 'error'"
            icon="fas fa-satellite"
          />
        </b-col>
        <b-col cols="12" md="6" lg="3" class="mb-3">
          <health-card
            service-name="Weather API"
            :status="servicesHealth.Weather || 'error'"
            icon="fas fa-cloud"
          />
        </b-col>
        <b-col cols="12" md="6" lg="3" class="mb-3">
          <health-card
            service-name="Navarea Service"
            :status="servicesHealth.Navarea || 'error'"
            icon="fas fa-compass"
          />
        </b-col>
      </b-row>

      <!-- Add/Edit Service Modal -->
      <service-modal
        v-model="showAddServiceModal"
        :service-data="selectedService"
        @save="saveService"
        @input="handleServiceModalClose"
      />
    </b-container>
  </div>
</template>

<script>
import HealthCard from '../components/HealthCard.vue'
import ServiceModal from '../components/ServiceModal.vue'
import { useServicesStore } from '@/stores/services'
import {
  getAllDevicesWithServices,
  pauseService,
  resumeService,
  unsubscribeFromService,
  getStatusInfo,
  isDateExpired,
  formatDate,
  getAllServicesHealth
} from '@/helpers/apiServices'

export default {
  name: 'ServicesTab',
  components: {
    HealthCard,
    ServiceModal
  },
  data() {
    return {
      isLoading: false,
      errorMessage: '',
      showAddServiceModal: false,
      selectedService: null,
      servicesHealth: {
        AIS: 'error',
        Navarea: 'error',
        Weather: 'error',
        Catalog: 'error'
      },
      healthCheckInterval: null,
      expandedDevices: {} // Track which devices are expanded
    }
  },
  computed: {
    servicesStore() {
      return useServicesStore()
    },
    devices() {
      return this.servicesStore.getAllDevices
    },
    storeError() {
      return this.servicesStore.error
    },
    tableItems() {
      const deviceMap = {}
      
      // Group services by device_id
      this.devices.forEach(device => {
        if (!deviceMap[device.device_id]) {
          deviceMap[device.device_id] = {
            device_id: device.device_id,
            ship_name: device.ship_name || device.shipName || '—',
            services: [],
            isExpanded: this.expandedDevices[device.device_id] || false,
            isHeader: true
          }
        }
        
        if (device.services && device.services.length > 0) {
          device.services.forEach(service => {
            // Determine effective status (consider expiration date)
            // If status is Canceled, keep it (higher priority than date expiration)
            // If date is expired and status is not Canceled, then show Expired
            // Otherwise show the status from DB
            let effectiveStatus = service.Status
            if (service.Status !== 'Canceled' && service.EndDate && isDateExpired(service.EndDate)) {
              effectiveStatus = 'Expired'
            }
            
            deviceMap[device.device_id].services.push({
              device_id: device.device_id,
              ship_name: device.ship_name || device.shipName || '—',
              service_type: service.ServiceType.replace(/-/g, ' '),
              status: effectiveStatus,
              end_date: formatDate(service.EndDate),
              end_date_raw: service.EndDate,
              notes: service.Notes,
              id: service.Id,
              start_date: service.StartDate,
              raw_service: service,
              isHeader: false
            })
          })
        }
      })
      
      // Build flat array with expanded items
      const items = []
      Object.values(deviceMap).forEach(deviceGroup => {
        // Add header row
        items.push(deviceGroup)
        
        // Add service rows if device is expanded
        if (deviceGroup.isExpanded && deviceGroup.services.length > 0) {
          items.push(...deviceGroup.services)
        }
      })
      
      return items
    }
  },
  watch: {
    storeError(newError) {
      if (newError) {
        this.errorMessage = newError
      }
    },
    showAddServiceModal(newVal) {
      // Убедиться что модальное окно может быть открыто снова
      if (!newVal) {
        // Модальное окно закрывается - очистить данные
        this.selectedService = null
      }
    }
  },
  mounted() {
    // Загрузить данные при монтировании компоненты
    this.loadDevices()
    this.loadServicesHealth()

    // Установить интервал для периодической проверки health статуса (каждые 30 сек)
    this.healthCheckInterval = setInterval(() => {
      this.loadServicesHealth()
    }, 30000)
  },
  beforeUnmount() {
    // Очистить интервал при размонтировании
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval)
    }
  },
  destroyed() {
    // Дополнительная очистка для Vue 2 (в случае если beforeUnmount не срабатывает)
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval)
      this.healthCheckInterval = null
    }
  },
  methods: {
    async loadDevices() {
      this.isLoading = true
      this.errorMessage = ''
      this.servicesStore.setLoading(true)

      try {
        const devicesData = await getAllDevicesWithServices()
        this.servicesStore.setDevices(devicesData)
      } catch (error) {
        const errorMsg = error.detail || error.message || 'Failed to load devices'
        this.errorMessage = errorMsg
        this.servicesStore.setError(errorMsg)
        console.error('Load error:', error)
      } finally {
        this.isLoading = false
        this.servicesStore.setLoading(false)
      }
    },
    async refreshAll() {
      // Обновить и devices и health статусы одновременно
      await Promise.all([
        this.loadDevices(),
        this.loadServicesHealth()
      ])
    },
    async loadServicesHealth() {
      try {
        const response = await getAllServicesHealth()
        if (response && response.services) {
          // Mapировать status -> компонента
          response.services.forEach(service => {
            const serviceKey = service.service_type
            // Преобразовать статус в нижний регистр
            const status = service.status?.toLowerCase() || 'error'
            this.servicesHealth[serviceKey] = status
          })
        }
      } catch (error) {
        console.error('Failed to load services health:', error)
        // При ошибке показать все как error
        Object.keys(this.servicesHealth).forEach(key => {
          this.servicesHealth[key] = 'error'
        })
      }
    },
    getStatusInfo(status) {
      return getStatusInfo(status)
    },
    getStatusBadge(status) {
      return this.getStatusInfo(status).badge
    },
    isDateExpired(date) {
      return isDateExpired(date)
    },
    editService(item) {
      this.selectedService = {
        ...item,
        isEdit: true
      }
      this.showAddServiceModal = true
    },
    async pauseServiceAction(item) {
      if (!confirm(`Pause ${item.service_type} for ${item.device_id}?`)) {
        return
      }

      this.isLoading = true
      this.servicesStore.setLoading(true)
      try {
        await pauseService(item.device_id, item.service_type.replace(/ /g, '-'))
        await this.loadDevices()
        this.$bvToast.toast('Service paused successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        })
      } catch (error) {
        const errorMsg = error.detail || error.message || 'Failed to pause service'
        this.errorMessage = errorMsg
        this.servicesStore.setError(errorMsg)
      } finally {
        this.isLoading = false
        this.servicesStore.setLoading(false)
      }
    },
    async resumeServiceAction(item) {
      if (!confirm(`Resume ${item.service_type} for ${item.device_id}?`)) {
        return
      }

      this.isLoading = true
      this.servicesStore.setLoading(true)
      try {
        await resumeService(item.device_id, item.service_type.replace(/ /g, '-'))
        await this.loadDevices()
        this.$bvToast.toast('Service resumed successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        })
      } catch (error) {
        const errorMsg = error.detail || error.message || 'Failed to resume service'
        this.errorMessage = errorMsg
        this.servicesStore.setError(errorMsg)
      } finally {
        this.isLoading = false
        this.servicesStore.setLoading(false)
      }
    },
    async unsubscribeAction(item) {
      if (!confirm(`Unsubscribe from ${item.service_type} for ${item.device_id}? This action cannot be undone.`)) {
        return
      }

      this.isLoading = true
      this.servicesStore.setLoading(true)
      try {
        await unsubscribeFromService(item.device_id, item.service_type.replace(/ /g, '-'))
        await this.loadDevices()
        this.$bvToast.toast('Service unsubscribed successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        })
      } catch (error) {
        const errorMsg = error.detail || error.message || 'Failed to unsubscribe'
        this.errorMessage = errorMsg
        this.servicesStore.setError(errorMsg)
      } finally {
        this.isLoading = false
        this.servicesStore.setLoading(false)
      }
    },
    async saveService() {
      this.showAddServiceModal = false
      await this.$nextTick()  // Ждать пока Vue обновит DOM
      this.selectedService = null
      await this.loadDevices()
    },
    handleServiceModalClose(value) {
      // Обработчик для любого закрытия модального окна
      // Это срабатывает когда:
      // 1. Нажимаем Cancel
      // 2. Нажимаем крестик (X)
      // 3. Тапим вне окна
      // 4. Нажимаем Escape
      this.showAddServiceModal = value
      if (!value) {
        // Окно закрывается - очистить состояние
        this.selectedService = null
      }
    },
    toggleDeviceExpand(deviceId) {
      this.$set(this.expandedDevices, deviceId, !this.expandedDevices[deviceId])
    },
    addServiceForDevice(deviceId) {
      this.selectedService = {
        device_id: deviceId,
        isEdit: false,
        isNew: true
      }
      this.showAddServiceModal = true
    },
    getRowClass(item) {
      return {
        'header-row': item.isHeader,
        'service-row': !item.isHeader,
        'expanded': item.isHeader && item.isExpanded
      }
    },
    sortBy(field) {
      // Placeholder for future sorting functionality
      console.log('Sort by:', field)
    },
    getServicesSummary(deviceGroup) {
      if (!deviceGroup.services || deviceGroup.services.length === 0) {
        return 'no services'
      }

      const statusCounts = {}
      deviceGroup.services.forEach(service => {
        const status = service.status.toLowerCase()
        statusCounts[status] = (statusCounts[status] || 0) + 1
      })

      // Check if all services have the same status
      const statuses = Object.keys(statusCounts)
      if (statuses.length === 1) {
        const status = statuses[0]
        const count = statusCounts[status]
        return `all ${status} (${count})`
      }

      // Multiple statuses - show summary
      const parts = []
      const statusPriority = ['active', 'paused', 'canceled', 'expired']
      statusPriority.forEach(status => {
        if (statusCounts[status]) {
          parts.push(`${statusCounts[status]} ${status}`)
        }
      })
      return parts.join(' / ')
    },
    getServicesSummaryVariant(deviceGroup) {
      if (!deviceGroup.services || deviceGroup.services.length === 0) {
        return 'secondary'
      }

      const statusCounts = {}
      deviceGroup.services.forEach(service => {
        const status = service.status.toLowerCase()
        statusCounts[status] = (statusCounts[status] || 0) + 1
      })

      // Determine color based on status
      const hasActive = statusCounts['active'] > 0
      const hasCanceled = statusCounts['canceled'] > 0
      const hasExpired = statusCounts['expired'] > 0
      const hasPaused = statusCounts['paused'] > 0

      // Priority: red (all canceled) > yellow (any canceled/expired) > green (all active) > secondary
      if (!hasActive && (hasCanceled || hasExpired)) {
        return 'danger' // All canceled or expired
      }
      
      if (hasCanceled || hasExpired) {
        return 'warning' // Mixed with canceled/expired
      }
      
      if (hasActive && !hasPaused && !hasCanceled && !hasExpired) {
        return 'success' // All active
      }

      return 'secondary'
    }
  }
}
</script>

<style scoped>
.services-tab {
  padding: 20px 0;
}

.section-header {
  padding-bottom: 15px;
  border-bottom: 2px solid #e9ecef;
  margin-bottom: 20px;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.section-title {
  color: #0066cc;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0;
}

.section-description {
  color: #666;
  font-size: 0.9rem;
  margin: 5px 0 0 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.services-table-wrapper {
  border: 1px solid #e4e8f5;
  border-radius: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #fbfcff 0%, #f6f8ff 100%);
  box-shadow: 0 4px 16px rgba(34, 45, 80, 0.07);
  overflow-x: auto;
}

.services-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0;
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.services-table thead {
  background: #eef1fb;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 0.72rem;
  color: #5c6685;
  font-weight: 600;
}

.services-table thead th {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 2px solid #d4dce9;
  cursor: pointer;
  user-select: none;
  vertical-align: middle;
}

.services-table thead th:hover {
  background: #e6eef5;
}

.services-table tbody tr {
  transition: transform 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
  border-bottom: 1px solid #eef1fb;
}

.services-table tbody tr:hover {
  background: #f9fbff;
}

.services-table tbody tr.header-row {
  background: #f6f8ff;
  font-weight: 600;
}

.services-table tbody tr.header-row:hover {
  background: #eef1fb;
  box-shadow: inset 0 2px 8px rgba(34, 45, 80, 0.05);
}

.services-table tbody tr.service-row {
  background: white;
}

.services-table tbody tr.service-row:hover {
  background: #fafbfe;
}

.services-table td {
  padding: 0.7rem;
  color: #1f2940;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
  font-size: 0.78rem;
  line-height: 1.35;
  vertical-align: middle;
}

/* Column widths */
.expand-col {
  width: 40px;
  text-align: center;
  padding: 0;
}

.device-id-col {
  width: 180px;
}

.ship-name-col {
  width: 150px;
}

.service-col {
  width: 140px;
}

.status-col {
  width: 120px;
}

.expires-col {
  width: 120px;
}

.actions-col {
  width: 200px;
}

/* Cell specific styles */
.expand-cell {
  text-align: center;
  padding: 0.5rem !important;
}

.expand-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  color: #0066cc;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  border-radius: 4px;
}

.expand-btn:hover {
  background: rgba(0, 102, 204, 0.1);
  color: #0052a3;
}

.device-id-cell {
  font-weight: 600;
}

.device-id {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.87rem;
  color: #1f2940;
}

.device-id code {
  background-color: rgba(98, 111, 149, 0.08);
  padding: 2px 6px;
  border-radius: 3px;
  color: #2d3b8f;
  font-family: 'Roboto Mono', 'Courier New', monospace;
  font-weight: 700;
  font-size: 0.8rem;
}

.indent-cell {
  padding-left: 2.5rem !important;
}

.indent-text {
  margin-left: 0.5rem;
}

.indent-text code {
  background-color: rgba(200, 200, 200, 0.1) !important;
  color: #666 !important;
}

.ship-name-cell {
  font-weight: 500;
}

.ship-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  color: #1f2940;
}

.service-cell {
  text-align: center;
}

.service-type-badge .badge {
  padding: 0.25rem 0.5rem !important;
  font-size: 0.7rem !important;
  font-weight: 700 !important;
  border-radius: 3px !important;
  display: inline-block !important;
  white-space: nowrap !important;
}

.services-count-badge {
  padding: 0.25rem 0.5rem !important;
  font-size: 0.7rem !important;
  font-weight: 700 !important;
  border-radius: 3px !important;
}

.status-summary-badge {
  padding: 0.25rem 0.5rem !important;
  font-size: 0.7rem !important;
  font-weight: 700 !important;
  border-radius: 3px !important;
  display: inline-block !important;
  white-space: nowrap !important;
}

.status-cell {
  text-align: center;
}

.status-badge .badge {
  padding: 0.25rem 0.5rem !important;
  font-size: 0.7rem !important;
  font-weight: 700 !important;
  border-radius: 3px !important;
  display: inline-block !important;
  white-space: nowrap !important;
}

.expires-cell {
  text-align: center;
  font-size: 0.75rem;
}

.actions-cell {
  text-align: center;
}

.action-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: center;
}

.action-buttons .btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  min-width: 32px;
}

.action-buttons .btn-sm i {
  font-size: 0.75rem;
}

.empty-state {
  border: 1px dashed #cdd3e5;
  border-radius: 12px;
  padding: 24px 16px;
  text-align: center;
  background: #f8f9fe;
  color: #5c6685;
}

.empty-icon {
  font-size: 3rem;
  color: #ccc;
  margin-bottom: 15px;
}

.empty-state h5 {
  color: #1f2940;
  font-weight: 600;
  margin-bottom: 10px;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
}

.empty-state p {
  color: #5c6685;
  margin-bottom: 0;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
  font-size: 0.78rem;
}

@media (max-width: 768px) {
  .header-top {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
  }

  .header-actions button {
    width: 100%;
  }

  .services-table-wrapper {
    padding: 12px;
    overflow-x: auto;
  }

  .services-table {
    font-size: 0.7rem;
  }

  .services-table td {
    padding: 0.5rem;
  }

  .device-id-col,
  .ship-name-col,
  .service-col,
  .status-col,
  .expires-col,
  .actions-col {
    width: auto !important;
  }

  .action-buttons {
    justify-content: center;
  }

  .action-buttons .btn-sm {
    padding: 0.2rem 0.4rem;
    font-size: 0.65rem;
  }

  .action-buttons .btn-sm i {
    font-size: 0.65rem;
  }
}
</style>
